/**
 * Sticker asset foundation + processing contract.
 *
 * Assets live inside `Project.data` (JSON canvas) and are persisted/versioned
 * through the existing autosave/revision plumbing. Media is converted on the
 * BACKEND (see the processing pipeline) into Telegram-ready output; the client
 * never does conversion. This module defines the asset shape, the input/output
 * constraints, and helpers to read/write the project data payload.
 */
import { isPackBrandingPlacement, type PackBrandingPlacement } from './pack.js';
import type { StickerType } from './policy.js';

export type StickerKind = 'image' | 'video';

/** Default emoji assigned to a freshly-added sticker (user can change it). */
export const DEFAULT_STICKER_EMOJI = '✨';

/**
 * Maximum emoji associations a single sticker may carry. This is Telegram's
 * real Bot API limit (verified empirically: `emoji_list` accepts 1–20 emoji;
 * 21+ → "too many emoji specified", 0 → "emoji list must be non-empty"). We do
 * NOT invent a stricter Astral-specific cap.
 */
export const MAX_STICKER_EMOJIS = 20;
/** Upper bound on a preset's ordered emoji sequence (Astral convenience cap). */
export const MAX_PRESET_EMOJIS = 50;

/** A single processed sticker belonging to a project. */
export interface StickerAsset {
  id: string;
  name: string;
  kind: StickerKind;
  /**
   * Processed source. Today this is a base64 data URL (legacy inline media).
   * Once media moves to the store it becomes a short-lived signed URL that is
   * derived on read — see {@link StickerAsset.media}, which is the authority on
   * where the BYTES actually come from.
   */
  src: string;
  /**
   * Where the bytes live. ABSENT means legacy inline (`src` holds them), which
   * is every asset written so far. Present-and-`asset` means the media store
   * owns them and `src` must not be used as a byte source.
   *
   * Declared here so publish/sync can read both formats; nothing writes it yet.
   */
  media?: import('./media.js').StickerMediaRef;
  /** Output MIME type ("image/png" | "video/webm"). */
  format: string;
  size: number;
  width: number;
  height: number;
  /** Video duration in ms (video kind only). */
  durationMs?: number;
  /**
   * The ordered emoji associations sent to Telegram for this sticker (1–20).
   * This is the source of truth; {@link StickerAsset.emoji} mirrors the first.
   */
  emojiList: string[];
  /**
   * Primary emoji (= emojiList[0]). Retained for display and backwards
   * compatibility with pre-emoji-list assets that stored only a scalar `emoji`.
   */
  emoji: string;
  /** Telegram file_id once this sticker exists in the published set (else undefined). */
  telegramFileId?: string;
  /**
   * Telegram's STABLE identifier for the published sticker.
   *
   * `file_id` may change; `file_unique_id` does not, which makes it the correct
   * key for deciding whether a remote sticker is still one of ours. Recorded
   * from `getStickerSet` at publish and at every sync. Absent on every sticker
   * published before this field existed — reconciliation falls back to
   * `telegramFileId` for those (see `computeRemoteSyncPlan`).
   */
  telegramFileUniqueId?: string;
  order: number;
  createdAt: string;
}

/**
 * Validate a SINGLE emoji association token for Telegram. Accepts real emoji
 * including combined ZWJ / flag (regional-indicator) / skin-tone / variation-
 * selector and keycap sequences; rejects empty values and plain text. Not
 * overly aggressive — the goal is to accept any valid device-keyboard emoji,
 * not to police against an Astral-curated whitelist.
 */
export function isValidEmoji(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const v = value.trim();
  if (v.length === 0 || v.length > 64) return false;
  // Keycap sequences (0-9 # *) legitimately contain an ASCII base char, so they
  // must be recognised before the plain-text rejection below.
  if (/^[0-9#*]️?⃣$/u.test(v)) return true;
  // Anything else containing ASCII letters/digits is text, not an emoji.
  if (/[A-Za-z0-9]/.test(v)) return false;
  return /\p{Extended_Pictographic}|\p{Regional_Indicator}/u.test(v);
}

/** Coerce a possibly-missing/invalid emoji to a safe value. */
export function coerceEmoji(value: unknown): string {
  return isValidEmoji(value) ? (value as string).trim() : DEFAULT_STICKER_EMOJI;
}

/**
 * Validate an ordered emoji list for a sticker: 1..{@link MAX_STICKER_EMOJIS}
 * entries, each a valid emoji. Used before publish/sync so an empty or
 * over-long association can never reach the Bot API.
 */
export function isValidEmojiList(value: unknown): value is string[] {
  return (
    Array.isArray(value) &&
    value.length >= 1 &&
    value.length <= MAX_STICKER_EMOJIS &&
    value.every((v) => isValidEmoji(v))
  );
}

/**
 * Coerce arbitrary input into a safe, non-empty sticker emoji list capped at
 * {@link MAX_STICKER_EMOJIS}. Falls back to `fallback` (or the default emoji)
 * when nothing valid remains — this is what migrates legacy scalar assets.
 */
export function coerceEmojiList(value: unknown, fallback?: string[]): string[] {
  const source = Array.isArray(value) ? value : [];
  const cleaned = source
    .filter((v): v is string => isValidEmoji(v))
    .map((v) => v.trim())
    .slice(0, MAX_STICKER_EMOJIS);
  if (cleaned.length > 0) return cleaned;
  if (fallback && fallback.length > 0) return fallback.slice(0, MAX_STICKER_EMOJIS);
  return [DEFAULT_STICKER_EMOJI];
}

/** Return an asset with its emoji list replaced (keeping the scalar `emoji` mirror in sync). */
export function setAssetEmojis(asset: StickerAsset, list: string[]): StickerAsset {
  const emojiList = coerceEmojiList(list);
  return { ...asset, emojiList, emoji: emojiList[0] };
}

/**
 * Cyclically assign an ordered emoji sequence to a set of target assets, using
 * the CURRENT VISUAL ORDER of the assets array. The first targeted asset (in
 * visual order) receives sequence item 0, the next item 1, wrapping around the
 * sequence until every target has one emoji. Non-targeted assets are untouched.
 *
 * Each assigned sticker gets a single-emoji association (the sequence item);
 * this is distinct from the advanced "multiple emoji per sticker" capability.
 */
export function assignSequenceToAssets(
  assets: StickerAsset[],
  targetIds: Iterable<string>,
  sequence: string[],
): StickerAsset[] {
  const seq = sequence.filter((e) => isValidEmoji(e));
  if (seq.length === 0) return assets;
  const targets = new Set(targetIds);
  let k = 0;
  return assets.map((asset) => {
    if (!targets.has(asset.id)) return asset;
    const emoji = seq[k % seq.length];
    k += 1;
    return { ...asset, emojiList: [emoji], emoji };
  });
}

/**
 * Sync state of a PUBLISHED project relative to its Telegram sticker set.
 * Independent from the project status (Draft/Active/Archived).
 */
export const SYNC_STATES = ['SYNCED', 'LOCAL_CHANGES', 'SYNCING', 'SYNC_FAILED'] as const;
export type SyncState = (typeof SYNC_STATES)[number];

// ---- Output (Telegram) constraints ----------------------------------------
export const STICKER_DIMENSION = 512;

/**
 * The format a NEWLY processed image sticker is written in.
 *
 * WebP, not PNG. Telegram's static-sticker slot accepts ".WEBP or .PNG" and is
 * itself served as WebP, so this is the platform's own format rather than a
 * conversion it tolerates — and it is strictly better here. Measured across the
 * test corpus, LOSSLESS WebP was smaller than lossless PNG on every single
 * image (e.g. a detailed photo: 182 KiB vs 474 KiB; transparent artwork:
 * 305 KiB vs 550 KiB), for bit-identical pixels. The practical effect is that
 * images which used to overflow the cap and get quantised to a 256-colour
 * palette now fit LOSSLESSLY instead.
 */
export const IMAGE_STICKER_FORMAT = 'image/webp';

/**
 * Every image format an asset may legitimately be stored in.
 *
 * PNG is retained because every sticker processed before the switch is a PNG
 * and must keep serving, publishing and syncing untouched — and because it is
 * still the last rung of the encoder ladder, for the rare image no WebP setting
 * can fit. Validation accepts the SET; only new output prefers the constant
 * above.
 */
export const IMAGE_STICKER_FORMATS = ['image/webp', 'image/png'] as const;
export type ImageStickerFormat = (typeof IMAGE_STICKER_FORMATS)[number];

export function isImageStickerFormat(value: unknown): value is ImageStickerFormat {
  return typeof value === 'string' && (IMAGE_STICKER_FORMATS as readonly string[]).includes(value);
}

export const IMAGE_STICKER_MAX_BYTES = 512 * 1024;

// NOTE on duration: Telegram's own video-sticker spec caps duration at 3
// seconds. Raising this to 5s would produce files Telegram rejects, so per the
// "do not violate Telegram constraints" rule we keep 3s (the hard platform
// limit), not a size limitation on our side.
// NOTE on maxBytes: 256 KiB is Telegram's own published video-sticker limit,
// not a margin this project added on top of it — so it is a hard ceiling to
// spend up to, never a conservative target to raise. The encoder aims just
// under it (see `media/video-encode.ts`); the budget was previously underspent
// by 60–80% because of the encoder settings, not because of this constant.
export const VIDEO_STICKER = {
  format: 'video/webm',
  dimension: 512,
  maxSeconds: 3,
  maxBytes: 256 * 1024,
  fps: 30,
} as const;

// ---- Accepted uploads ------------------------------------------------------
export const ACCEPTED_IMAGE_INPUT = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'] as const;
export const ACCEPTED_VIDEO_INPUT = ['video/mp4', 'video/webm', 'video/quicktime'] as const;
/** Hard cap on the raw upload the backend will accept before processing. */
export const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

/** Which sticker kinds a project of the given type may contain. */
export function kindsForType(type: StickerType): StickerKind[] {
  if (type === 'IMAGE') return ['image'];
  if (type === 'VIDEO') return ['video'];
  return ['image', 'video'];
}

/** The HTML file-input `accept` attribute for a project type. */
export function acceptForType(type: StickerType): string {
  const kinds = kindsForType(type);
  const list: string[] = [];
  if (kinds.includes('image')) list.push(...ACCEPTED_IMAGE_INPUT);
  if (kinds.includes('video')) list.push(...ACCEPTED_VIDEO_INPUT);
  return list.join(',');
}

/** Classify an input MIME type into a sticker kind (null if unsupported). */
export function kindForMime(mime: string): StickerKind | null {
  if ((ACCEPTED_IMAGE_INPUT as readonly string[]).includes(mime)) return 'image';
  if ((ACCEPTED_VIDEO_INPUT as readonly string[]).includes(mime)) return 'video';
  return null;
}

// ---- Output validation (server asserts processed output) ------------------
export type StickerValidationCode = 'INVALID_FORMAT' | 'TOO_LARGE' | 'BAD_DIMENSIONS' | 'TOO_LONG';

export interface ProcessedAssetInput {
  kind: StickerKind;
  format: string;
  size: number;
  width: number;
  height: number;
  durationMs?: number;
}

export interface StickerValidationResult {
  ok: boolean;
  codes: StickerValidationCode[];
}

/** Assert a processed asset meets Telegram's constraints for its kind. */
export function validateProcessedAsset(input: ProcessedAssetInput): StickerValidationResult {
  const codes: StickerValidationCode[] = [];
  const square = input.width === STICKER_DIMENSION && input.height === STICKER_DIMENSION;
  if (!square) codes.push('BAD_DIMENSIONS');

  if (input.kind === 'image') {
    // The SET, not the preferred format: PNG remains valid for every asset
    // written before the WebP switch, and for the encoder's last-resort rung.
    if (!isImageStickerFormat(input.format)) codes.push('INVALID_FORMAT');
    if (input.size > IMAGE_STICKER_MAX_BYTES) codes.push('TOO_LARGE');
  } else {
    if (input.format !== VIDEO_STICKER.format) codes.push('INVALID_FORMAT');
    if (input.size > VIDEO_STICKER.maxBytes) codes.push('TOO_LARGE');
    if ((input.durationMs ?? 0) > VIDEO_STICKER.maxSeconds * 1000 + 100) codes.push('TOO_LONG');
  }
  return { ok: codes.length === 0, codes };
}

// ---- Project data payload --------------------------------------------------
/**
 * A project's optional default emoji behaviour. When `autoApply` is set, newly
 * added (incl. batch-uploaded) stickers receive the `emojis` sequence cyclically
 * instead of the plain default. Stored inside `project.data` (the JSON canvas),
 * so it persists through autosave without touching the publishing schema. This
 * is a TEMPLATE — it never rewrites already-assigned stickers unless the user
 * explicitly applies it.
 */
export interface ProjectEmojiPreset {
  /** Optional name (when derived from a saved user preset). */
  name?: string;
  /** Ordered emoji sequence; empty/`[✨]` means "single default". */
  emojis: string[];
  /** Whether new stickers should auto-receive this sequence. */
  autoApply: boolean;
}

export interface StickerProjectData {
  notes: string;
  assets: StickerAsset[];
  /** Project-level default emoji preset (optional). */
  emojiPreset?: ProjectEmojiPreset | null;
  /**
   * Where the Astral tag sits in the published DISPLAY title.
   *
   * The user's STORED preference — not necessarily what gets published. The
   * server resolves it against the current role + plan at publish time (see
   * resolvePackBranding), so a Free or lapsed account still publishes with the
   * tag while the saved choice is left intact for when entitlement returns.
   *
   * Lives in the project JSON alongside emojiPreset, so no schema change.
   */
  packBranding?: PackBrandingPlacement;
}

function readProjectPreset(value: unknown): ProjectEmojiPreset | null {
  if (typeof value !== 'object' || value === null) return null;
  const raw = value as Partial<ProjectEmojiPreset>;
  const emojis = Array.isArray(raw.emojis) ? raw.emojis.filter((e) => isValidEmoji(e)) : [];
  if (emojis.length === 0) return null;
  return {
    name: typeof raw.name === 'string' ? raw.name : undefined,
    emojis: emojis.slice(0, MAX_PRESET_EMOJIS),
    autoApply: raw.autoApply === true,
  };
}

export function readStickerData(data: Record<string, unknown>): StickerProjectData {
  const notes = typeof data.notes === 'string' ? data.notes : '';
  const rawAssets = Array.isArray(data.assets) ? (data.assets as unknown[]) : [];
  const assets = rawAssets
    .filter((a): a is StickerAsset => {
      return (
        typeof a === 'object' &&
        a !== null &&
        typeof (a as StickerAsset).id === 'string' &&
        typeof (a as StickerAsset).src === 'string'
      );
    })
    .map((a) => {
      // Back-compat: migrate a legacy scalar `emoji` into `emojiList`; assets
      // predating the emoji system get the default. New assets use emojiList.
      const emojiList = coerceEmojiList(
        (a as StickerAsset).emojiList,
        (a as { emoji?: unknown }).emoji ? [coerceEmoji((a as StickerAsset).emoji)] : undefined,
      );
      return {
        ...a,
        kind: a.kind === 'video' ? 'video' : 'image',
        emojiList,
        emoji: emojiList[0],
      } as StickerAsset;
    })
    .sort((a, b) => a.order - b.order);
  return {
    notes,
    assets,
    emojiPreset: readProjectPreset(data.emojiPreset),
    // Absent on every project predating the field; undefined resolves to the
    // safe default rather than silently dropping the tag.
    ...(isPackBrandingPlacement(data.packBranding) ? { packBranding: data.packBranding } : {}),
  };
}

export function writeStickerData(data: StickerProjectData): Record<string, unknown> {
  return {
    notes: data.notes,
    assets: data.assets.map((asset, index) => {
      const emojiList = coerceEmojiList(
        asset.emojiList,
        asset.emoji ? [coerceEmoji(asset.emoji)] : undefined,
      );
      /*
       * For asset-backed stickers `src` is a DERIVED, short-lived signed URL
       * that the server mints on every read. Blank it on the way out so a
       * signed link is never persisted (it would expire in storage) and so a
       * URL refresh does not count as an edit worth autosaving.
       */
      const src = asset.media?.kind === 'asset' ? '' : asset.src;
      return { ...asset, src, order: index, emojiList, emoji: emojiList[0] };
    }),
    ...(data.emojiPreset && data.emojiPreset.emojis.length > 0
      ? { emojiPreset: data.emojiPreset }
      : {}),
    // Persist only a recognised value, so a hand-edited or stale payload can
    // never smuggle an arbitrary placement through.
    ...(isPackBrandingPlacement(data.packBranding) ? { packBranding: data.packBranding } : {}),
  };
}
